import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import PauseIcon from '@mui/icons-material/Pause';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { changeFunnelStatus } from 'API/linkedin-funnel';
import { Routes } from 'enums';
import { FunnelStatuses } from 'features/co-pilot/co-pilot.types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useWizardCrumbStore from 'stores/wizard-crumb-store';
import { getRun } from './campaign-wizard.api';
import { fmtNumber } from './campaign-wizard.logic';
import { RunSnapshot } from './campaign-wizard.types';
import { wz } from './components/wizard-tokens';
import RunningCampaignNotice from './components/running-campaign-notice';
import { MiSpinner, WizGradientButton } from './components/wizard-ui';
import { approvalLabelKey, isPaused, runStatusKey, runStepView } from './run/run-model';
import type { WizardController } from './use-campaign-wizard';

const POLL_MS = 5000;

const SETTING_ICONS: Record<string, React.ElementType> = {
  person: PersonOutlineIcon,
  workspace_premium: WorkspacePremiumOutlinedIcon,
  forum: ForumOutlinedIcon,
  schedule: ScheduleOutlinedIcon,
  mark_email_read: MarkEmailReadOutlinedIcon,
  history: HistoryOutlinedIcon,
};

const STEP_ICONS = {
  check_circle: CheckCircleIcon,
  autorenew: AutorenewIcon,
  radio_button_unchecked: RadioButtonUncheckedIcon,
} as const;

const card: React.CSSProperties = {
  background: '#FFF',
  borderRadius: 12,
  boxShadow: wz.shadowCard,
};

/** „Kampagne gestartet“: was jetzt passiert, Kennzahlen und Einstellungen des Agenten. */
const RunView: React.FC<{ wizard: WizardController }> = ({ wizard }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { campaignId } = wizard;
  const [run, setRun] = useState<RunSnapshot | null>(wizard.run);
  const [loadFailed, setLoadFailed] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [pausing, setPausing] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);

  const setCrumbName = useWizardCrumbStore(state => state.setCampaignName);
  useEffect(() => {
    setCrumbName(run?.name ?? null);
    return () => setCrumbName(null);
  }, [run?.name, setCrumbName]);

  useEffect(() => {
    if (wizard.run) setRun(wizard.run);
  }, [wizard.run]);

  useEffect(() => {
    if (!campaignId) return undefined;
    let cancelled = false;
    const load = () =>
      getRun(campaignId)
        .then(snap => {
          if (cancelled) return;
          setRun(snap);
          setLoadFailed(false);
        })
        .catch(() => !cancelled && setLoadFailed(true));
    void load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [campaignId]);

  const toOverview = useCallback(() => {
    navigate(Routes.RECRUITING_BOOSTER_CO_PILOT_PROJECTS, { state: { newCampaignId: campaignId } });
  }, [campaignId, navigate]);

  const toTasks = useCallback(() => navigate(Routes.RECRUITING_BOOSTER_CO_PILOT_TASKS), [navigate]);

  const togglePause = useCallback(async () => {
    if (!campaignId || !run) return;
    setPausing(true);
    setStatusError(null);
    const res = await changeFunnelStatus(
      campaignId,
      isPaused(run.status) ? FunnelStatuses.STARTED : FunnelStatuses.PAUSED
    );
    if (res?.error) {
      setStatusError(res.error?.message ?? t('wizRunStatusError'));
    } else {
      try {
        setRun(await getRun(campaignId));
      } catch {
        setLoadFailed(true);
      }
    }
    setPausing(false);
  }, [campaignId, run, t]);

  if (!run) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
        }}
      >
        {loadFailed ? (
          <div style={{ color: wz.redFg }}>{t('wizRunLoadError')}</div>
        ) : (
          <MiSpinner size={32} />
        )}
        <div style={{ color: wz.muted, fontSize: 14 }}>{loadFailed ? '' : t('wizRunLoading')}</div>
      </div>
    );
  }

  const status = runStatusKey(run.status);
  const paused = isPaused(run.status);
  const stats = [
    { label: t('wizRunStatFound'), value: fmtNumber(run.stats.found) },
    { label: t('wizRunStatFit'), value: fmtNumber(run.stats.fit) },
    { label: t('wizRunStatContacted'), value: fmtNumber(run.stats.contacted) },
  ];

  return (
    <div
      style={{
        flex: 1,
        overflow: 'auto',
        padding: '24px 40px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
      }}
    >
      <button
        type="button"
        onClick={toOverview}
        style={{
          fontSize: 14,
          color: wz.primary,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          cursor: 'pointer',
          width: 'fit-content',
          background: 'transparent',
          border: 'none',
          padding: 0,
          fontFamily: 'inherit',
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 18 }} />
        {t('wizBackToCampaigns')}
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 26, fontWeight: 500, letterSpacing: '-0.2px' }}>{run.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                borderRadius: 10,
                padding: '2px 10px',
                fontSize: 14,
                background: status === 'working' ? wz.greenBg : wz.border,
              }}
            >
              {status === 'working'
                ? t('wizRunWorking')
                : paused
                  ? t('wizRunPausedPill')
                  : /^(draft|created)$/i.test(run.status)
                    ? t('wizRunDraftPill')
                    : /^(finished|completed|done)$/i.test(run.status)
                      ? t('wizRunDonePill')
                      : run.status}
            </div>
            {run.pendingApproval > 0 ? (
              <button
                type="button"
                onClick={toTasks}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '3px 10px',
                  borderRadius: 8,
                  background: wz.warnBg,
                  border: '1px solid #ED6C02',
                  color: wz.warnFg,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <MarkEmailUnreadOutlinedIcon sx={{ fontSize: 16 }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {t(approvalLabelKey(run.pendingApproval), { count: run.pendingApproval })}
                </span>
              </button>
            ) : (
              <div style={{ fontSize: 13, color: wz.muted }}>{t(approvalLabelKey(0))}</div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            onClick={() => void togglePause()}
            disabled={pausing || status === 'other'}
            style={{
              height: 40,
              padding: '0 14px',
              boxSizing: 'border-box',
              border: `1px solid ${wz.dashed}`,
              borderRadius: 8,
              background: '#FFF',
              fontSize: 14,
              color: wz.text555,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              fontFamily: 'inherit',
              opacity: status === 'other' ? 0.5 : 1,
            }}
          >
            {paused ? <PlayArrowIcon sx={{ fontSize: 18 }} /> : <PauseIcon sx={{ fontSize: 18 }} />}
            {paused ? t('wizRunResume') : t('wizRunPause')}
          </button>
          <WizGradientButton onClick={toOverview}>{t('wizRunToOverview')}</WizGradientButton>
        </div>
      </div>
      {statusError ? <div style={{ color: wz.redFg, fontSize: 13 }}>{statusError}</div> : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1.6fr) minmax(0,1fr)',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <div
          style={{
            ...card,
            padding: '24px 28px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 12 }}>
            {t('wizRunWhatNow')}
          </div>
          {run.steps.map((step, i) => {
            const view = runStepView(step);
            const Icon = STEP_ICONS[view.icon];
            return (
              <div key={`${i}-${step.title}`} style={{ display: 'flex', gap: 14 }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: 24,
                    flexShrink: 0,
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 24,
                      color: view.iconColor,
                      ...(view.spin
                        ? {
                            animation: 'wizSpin 1.4s linear infinite',
                            '@keyframes wizSpin': { to: { transform: 'rotate(360deg)' } },
                          }
                        : {}),
                    }}
                  />
                  {i < run.steps.length - 1 ? (
                    <div
                      style={{
                        flex: 1,
                        width: 2,
                        background: wz.border,
                        margin: '4px 0',
                        minHeight: 18,
                      }}
                    />
                  ) : null}
                </div>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    paddingBottom: 18,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 700, color: view.titleColor }}>
                    {step.title}
                  </div>
                  <div style={{ fontSize: 14, color: wz.muted }}>{step.sub}</div>
                  {view.barPercent !== null ? (
                    <div
                      style={{
                        height: 6,
                        borderRadius: 999,
                        background: wz.border,
                        overflow: 'hidden',
                        maxWidth: 360,
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          background: wz.primary,
                          transition: 'width 600ms',
                          width: `${view.barPercent}%`,
                        }}
                      />
                    </div>
                  ) : null}
                  {view.approveButton ? (
                    <button
                      type="button"
                      onClick={toTasks}
                      style={{
                        marginTop: 4,
                        height: 32,
                        padding: '0 14px',
                        boxSizing: 'border-box',
                        borderRadius: 6,
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#FFF',
                        background: wz.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        width: 'fit-content',
                        cursor: 'pointer',
                        border: 'none',
                        fontFamily: 'inherit',
                      }}
                    >
                      {t('wizRunApproveNow')}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            style={{
              ...card,
              padding: '20px 24px',
              display: 'grid',
              gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
              gap: 12,
            }}
          >
            {stats.map(stat => (
              <div key={stat.label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 24, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 13, color: wz.muted }}>{stat.label}</div>
              </div>
            ))}
          </div>
          <div
            style={{
              ...card,
              padding: '20px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{t('wizRunAgentTitle')}</div>
              <button
                type="button"
                onClick={() =>
                  navigate({
                    pathname: Routes.RECRUITING_BOOSTER_CO_PILOT_CAMPAIGN,
                    search: `?_id=${run.campaignId}`,
                  })
                }
                style={{
                  fontSize: 14,
                  color: wz.primary,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  fontFamily: 'inherit',
                }}
              >
                {t('wizRunChange')}
              </button>
            </div>
            {run.settings.map(setting => {
              const Icon = SETTING_ICONS[setting.icon] ?? InfoOutlinedIcon;
              return (
                <div
                  key={setting.text}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: 14,
                    color: wz.text555,
                  }}
                >
                  <Icon sx={{ fontSize: 18, color: wz.muted }} />
                  {setting.text}
                </div>
              );
            })}
          </div>
          <div
            style={{
              ...card,
              padding: '16px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
            data-testid="wiz-run-change"
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{t('wizRunChangeCampaignTitle')}</div>
              <button
                type="button"
                onClick={() => setChangeOpen(open => !open)}
                aria-expanded={changeOpen}
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: wz.primary,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  fontFamily: 'inherit',
                }}
              >
                {t('wizRunChangeCampaign')}
              </button>
            </div>
            {changeOpen ? (
              <RunningCampaignNotice compact onDuplicate={wizard.duplicateCampaign} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunView;
